/**
 * Copyright (C) IRT GmbH 2020
 *
 * @author Michael Probst
 */

var EventEmitter = require('events');

var QS = require('hbbtv-lib/tools/query_string').get;
var ati_testenv = QS("debug") || QS("atitest");

var storage = require('./local-storage');
var F = require("./functions");

var EVENT1 = "App-Start HbbTV";
var EVENT2 = "App-Verbindung Mobil";
var EVENT3 = "App-Tonspur";
var EVENT4 = "HbbTV-Tonspur";

var CUSTOM_VARS_DEFAULTS = {
    1 : "Mediathek",
    14 : "HbbTV"
}

var PAGE_DEFAULTS = {
    name : null,
    level2 : "13",
    chapter1: "Mediathek",
    chapter2: "Dialog plus"
};

var CONST_ATITRACKING_DISABLED = "ATITRACKINGDISABLED";

/**
 * @typedef PageProps
 * @property {string} name same as #7 of CustomVarsProps
 * @property {string} level2 "13"
 * @property {string} chapter1 "Mediathek"
 * @property {string} chapter2 "Dialog Plus"
 */

/**
 * @typedef CustomVarsProps
 * @property {string} 1 "Mediathek"
 * @property {string} 2 Sender (Service Name)
 * @property {string} 5 Series/Sendereihe (only if audio track selected, else "Keine Sendereihe")
 * @property {string} 6 Typ - event type + name/title of audio track
 * @property {string} 7 Name of episode (Short Description) if audio track selected, else event type
 * @property {string} 8 same as 7
 * @property {string} 11 DVB Service URL
 * @property {string} 14 "HbbTV"
 * @property {string} 16 "Kabel", "Satellit", "DVB-T"
 */

function clone(obj) {
    if (null == obj || "object" != typeof obj) return obj;
    var copy = obj.constructor();
    for (var attr in obj) {
        if (obj.hasOwnProperty(attr)) copy[attr] = obj[attr];
    }
    return copy;
}

function ATITracking() {

    /**
     * @type {import ('events').EventEmitter}
     */
    var emitter = new EventEmitter();

    function debug(text) {
        emitter.emit("debug", text);
        if (console) console.log("ATITRACKING", text);
    }

    var _tag = null;

    var enabled = false;

    var loading = false;
    var loaded = false;
    var evtQueue = [];

    var failed = null;

    var bcSvcName = null;
    var bcSvcId   = null;
    var programmeID = null;       // EIT id
    var programmeVPSID = null;    // VPS Label
    var programmeName = null;     // Sendereihe
    var programmeDescr = null;
    var deliverySystem = null;    // Kabel, Satellit, Terrestrik

    storage.on(CONST_ATITRACKING_DISABLED, function (val) {
        setEnabled(val === null);
    });

    /**
     *
     * @param {boolean} b true to enable tracking, false to disable it.
     */
    function setEnabled(b) {
        debug("setEnabled: current " + enabled + " - next " + b);

        if (b !== enabled) {
            enabled = b;

            // if (enabled) storage.remove(CONST_ATITRACKING_DISABLED);
            // else storage.set(CONST_ATITRACKING_DISABLED, CONST_ATITRACKING_DISABLED);

            if (enabled && !(loading || loaded)) {
                load();
            }
        }
    }

    /**
     * Loads the ATI script. If loading finished successfully, calls waiting event functions.
     */
    function load() {
        try {
            if (!loading && !loaded && storage.get(CONST_ATITRACKING_DISABLED) === null) {
                var script = document.createElement("script");
                script.setAttribute("type", "text/javascript");
                script.setAttribute("src", "//tag.aticdn.net/" + (ati_testenv ? "596276" : "596277") + "/smarttag.js");
                script.onload = function () {
                    debug( "loaded: " + script.getAttribute("src"));
                    loaded = true;
                    loading = false;
                    for (x in evtQueue) {
                        evtQueue[x]();
                    }
                    evtQueue = [];
                };
                script.onerror = function (e) {
                    debug( "error loading ATI script. " + e);
                    loading = false;
                }
                document.getElementsByTagName("head")[0].appendChild(script);
                loading = true;
                debug( "loading: " + script.getAttribute("src"));
            } else {
                debug( "not loading: " + storage.get(CONST_ATITRACKING_DISABLED));
            }
        } catch (e) {
            debug( "exc in load(): " + e);
        }
    }

    /**
     * Lazy init for ATI Tracker Tag.
     */
    function tag() {
        if (_tag === null) {
            try {
                _tag = new ATInternet.Tracker.Tag({ site : ati_testenv ? "596276" : "596277" });
            } catch (e) {
                _tag = false;
                failed = e;
            }
        }
        return _tag;
    }

    /**
     * Run encodeURIComponent on all string properties' values.
     *
     * @param {object} obj
     * @returns {object} the object passed in as argument
     */
    function enc(obj) {
        for (x in obj) {
            if (typeof obj[x] == "string")
                obj[x] = encodeURIComponent(obj[x]);
        }
        return obj;
    }

    /**
     * Surround every string properties' value with brackets "[]"
     *
     * @param {object} obj
     * @returns {object} the modified object passed in as argument.
     */
    function brackets(obj) {
        for (x in obj) {
            if (typeof obj[x] == "string")
                obj[x] = "[" + obj[x] + "]";
        }
        return obj;
    }

    function sendEvent(pageProps, customVarProps) {
        if (loaded) {
            sendEvent0(pageProps, customVarProps);
        } else {
            emit("debug", "queue ATI event when ATI lib is loaded or in case user has opt out but changes his mind.");
            evtQueue.push(sendEvent0.bind(null, pageProps, customVarProps));
        }
    }

    /**
     * Sends an event to ATI.
     *
     * @param {PageProps} pageProps required
     * @param {CustomVarsProps} customVarProps optional
     * @returns {boolean} false if either ATI library is not available or pageProps is not a proper object
     */
    function sendEvent0(pageProps, customVarProps) {
        try {
            debug( "pageProps: " + JSON.stringify(pageProps));
            debug( "customVars: " + JSON.stringify(customVarProps));

            if (tag() && pageProps && pageProps.name) {
                tag().page.set(enc(pageProps));
                if (customVarProps) {
                    tag().customVars.set( { site : enc(customVarProps) });
                }
                tag().dispatch();

                debug( "dispatched event.");
                return true;
            } else {
                debug( "Can't send event: " + failed);
            }
            return false;
        } catch (e) {
            debug( "exc in sendEvent: " + e);
        }
    }

    function send(atiEvent, opt) {
        if (enabled) {
            var page = clone(PAGE_DEFAULTS);
            page.name = atiEvent;

            var customVars = clone(CUSTOM_VARS_DEFAULTS);
            customVars["2"] = bcSvcName;
            customVars["5"] = "keine Sendereihe";
            customVars["7"] = atiEvent;
            customVars["8"] = atiEvent;
            customVars["11"] = bcSvcId;
            customVars["12"] = "";
            customVars["16"] = deliverySystem;

            function setProgrammeVars (eventType) {
                customVars["5"] = programmeName;
                customVars["6"] = eventType + " - " + opt.slaveId;
                for (x in material.companionstreams) {
                    if (material.companionstreams[x].id == opt.slaveId) {
                        customVars["6"] = eventType + " - " + material.companionstreams[x].title;
                        break;
                    }
                }
                page.name = customVars["7"] = customVars["8"] = programmeDescr;
                customVars["12"] = programmeVPSID;
            }

            switch (atiEvent) {
                case EVENT1:
                case EVENT2:
                    customVars["6"] = atiEvent;
                    break;
                case EVENT3:
                case EVENT4:
                    setProgrammeVars(atiEvent);
                    break;
            }

            sendEvent(page, brackets(customVars));
        } else {
            debug("ignore event, tracking disabled.");
        }
    }

    function deliveryNetwork (idType) {
        switch (idType) {
        case 10: // ID_DVB_C
        case 14: // ID_DVB_C2
            return "Kabel";
        case 11: // ID_DVB_S
        case 15: // ID_DVB_S2
            return "Satellit";
        case 12: // ID_DVB_T
        case 16: // ID_DVB_T2
            return "DVB-T";
        case 40: // ID_IPTV_SDS
        case 41: // ID_IPTV_URI
            return "IPTV " + idType;
        default:
            return "NOTSUPPORTED: " + idType;
        }
    }

    /**
     *
     * @param {VideoBroadcastObject} vbo
     * @param {Event} evt
     */
    function updateProgramme(vbo, evt) {
        try {
            if (vbo.playState == 2) {
                var c = vbo.currentChannel;
                var p = vbo.programmes[0];

                if (p.programmeID != programmeID) {

                    programmeID = p.programmeID;
                    programmeName = F.stripEpisodeFromTitle(p.name);
                    programmeDescr = p.description || p.name;
                    bcSvcId = programmeID.split(";")[0];
                    bcSvcName = bcSvcName || c.name;
                    deliverySystem = deliveryNetwork(c.idType);

                    debug( "Channel: " + bcSvcName + " - " + bcSvcId + " - " + c.idType + ": " + deliverySystem + " - NID: " + c.nid);
                    debug( "Programme: " +
                        new Date(p.startTime * 1000).toLocaleString() + " - " +
                        new Date((p.startTime + p.duration) * 1000).toLocaleTimeString() + " - " + p.programmeID + " - " +
                        (p.name + " - " + p.description).replace("&", "&amp;"));
                }

            } else {
                debug( "ATITracking: VBObject not in presenting state.");
            }
        } catch (e) {
            debug( "exc: ATITracking: " + e + " - " + evt);
        }
    }

    /**
     * MaterialInfo for the current programme.
     *
     * @param {*} _material
     */
    function updateSyncMaterial (_material) {
        if (typeof _material == "object") {
            material = _material;
            programmeVPSID = material.eventid;
            bcSvcName = material.svcName || bcSvcName;

            debug( "Material: " + bcSvcName + " - " + _material.eventid + " - " + _material.companionstreams.length);
        }
    }

    setEnabled(storage.get(CONST_ATITRACKING_DISABLED) === null);

    return {
        CONST_ATITRACKING_DISABLED : CONST_ATITRACKING_DISABLED,

        EVENT1 : EVENT1,
        EVENT2 : EVENT2,
        EVENT3 : EVENT3,
        EVENT4 : EVENT4,

        on : emitter.on.bind(emitter),
        off : emitter.off.bind(emitter),
        once : emitter.once.bind(emitter),

        setEnabled : setEnabled,
        send : send,
        updateProgramme : updateProgramme,
        updateSyncMaterial  : updateSyncMaterial
    };
}

module.exports = ATITracking();
